const iconSize = 512;
const outputType = "image/webp";
const outputQuality = 0.92;

export type PreparedIcon = {
  dataUrl: string;
  sourceUrl?: string;
  width: number;
  height: number;
  filename: string;
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const fileFromBlob = (blob: Blob, filename: string): File => {
  return new File([blob], filename, { type: blob.type || "image/png" });
};

const extensionForImageType = (contentType: string): string => {
  const normalized = contentType.split(";")[0]?.trim().toLowerCase();
  if (normalized === "image/jpeg") return "jpg";
  if (normalized === "image/x-icon" || normalized === "image/vnd.microsoft.icon") return "ico";
  if (normalized === "image/svg+xml") return "svg";
  return normalized?.replace("image/", "") || "png";
};

const loadImage = async (source: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("画像を読み込めませんでした。"));
    image.src = source;
  });

const canvasToDataUrl = (canvas: HTMLCanvasElement): string => {
  const encoded = canvas.toDataURL(outputType, outputQuality);
  return encoded.startsWith("data:image/webp") ? encoded : canvas.toDataURL("image/png");
};

export const upscaleImageFile = async (file: File, sourceUrl?: string): Promise<PreparedIcon> => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = iconSize;
    canvas.height = iconSize;
    const context = canvas.getContext("2d", { alpha: true });
    if (!context) throw new Error("Canvasを初期化できませんでした。");

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.clearRect(0, 0, iconSize, iconSize);

    const sourceWidth = image.naturalWidth || iconSize;
    const sourceHeight = image.naturalHeight || iconSize;
    const scale = Math.min(iconSize / sourceWidth, iconSize / sourceHeight);
    const targetWidth = Math.round(sourceWidth * scale);
    const targetHeight = Math.round(sourceHeight * scale);
    const x = Math.round((iconSize - targetWidth) / 2);
    const y = Math.round((iconSize - targetHeight) / 2);

    context.drawImage(image, x, y, targetWidth, targetHeight);

    return {
      dataUrl: canvasToDataUrl(canvas),
      sourceUrl,
      width: sourceWidth,
      height: sourceHeight,
      filename: file.name || "icon.webp"
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const fetchFaviconFile = async (pageUrl: string): Promise<{ file: File; sourceUrl: string }> => {
  const parsed = new URL(pageUrl);
  if (!["http:", "https:"].includes(parsed.protocol)) {
    throw new Error("http または https のURLを入力してください。");
  }

  const response = await fetch(`/api/favicon?url=${encodeURIComponent(parsed.toString())}`, {
    headers: { accept: "image/*" }
  });
  if (!response.ok) {
    let message = "faviconを取得できませんでした。";
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      message = `favicon取得に失敗しました: HTTP ${response.status}`;
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  if (!blob.type.startsWith("image/") || blob.size === 0) throw new Error("faviconが画像ではありません。");

  const sourceUrl = response.headers.get("x-favicon-source") || `${parsed.origin}/favicon.ico`;
  const extension = extensionForImageType(blob.type);
  return { file: fileFromBlob(blob, `favicon-${parsed.hostname}.${extension}`), sourceUrl };
};

export const upscaleWithServer = async (dataUrl: string): Promise<string> => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 120000);

  const formData = new FormData();
  formData.append("file", fileFromBlob(await dataUrlToBlob(dataUrl), "icon.webp"));
  formData.append("model", "photo");
  formData.append("scale", "2");
  formData.append("format", "png");

  try {
    const response = await fetch("/api/upscale", {
      method: "POST",
      body: formData,
      signal: controller.signal
    });
    if (!response.ok) {
      let message = `アップスケールに失敗しました: HTTP ${response.status}`;
      try {
        const body = (await response.json()) as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // Keep the HTTP status fallback.
      }
      throw new Error(message);
    }
    const output = await response.blob();
    if (!output.type.startsWith("image/") || output.size === 0) throw new Error("アップスケール結果が画像ではありません。");
    const normalized = await upscaleImageFile(fileFromBlob(output, "icon-upscaled.png"));
    return normalized.dataUrl;
  } finally {
    window.clearTimeout(timeout);
  }
};
